/**
 * Tooltip - Minimalist Hover Tooltip Component
 *
 * Pure CSS-based tooltip for displaying helpful text on hover.
 * No external dependencies, accessible, and performant.
 *
 * @example
 * <Tooltip content="Edit listing">
 *   <Button size="icon"><Edit /></Button>
 * </Tooltip>
 */
import { useState } from "react";
import { cn } from "@/shared/lib/utils/cn";

export interface TooltipProps {
  /** Tooltip text content */
  content: string;
  /** Element(s) to wrap with tooltip */
  children: React.ReactNode;
  /** Tooltip position relative to trigger */
  side?: "top" | "bottom" | "left" | "right";
  /** Additional CSS classes */
  className?: string;
}

/**
 * Tooltip component with hover trigger
 */
export function Tooltip({
  content,
  children,
  side = "top",
  className,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {/* Trigger element */}
      <div>{children}</div>

      {/* Tooltip bubble */}
      {isVisible && (
        <div
          role="tooltip"
          className={cn(
            "absolute z-50 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded shadow-lg whitespace-nowrap pointer-events-none",
            "animate-in fade-in-0 zoom-in-95",
            {
              "bottom-full left-1/2 -translate-x-1/2 mb-2": side === "top",
              "top-full left-1/2 -translate-x-1/2 mt-2": side === "bottom",
              "right-full top-1/2 -translate-y-1/2 mr-2": side === "left",
              "left-full top-1/2 -translate-y-1/2 ml-2": side === "right",
            },
            className,
          )}
        >
          {content}

          {/* Arrow pointer */}
          <div
            className={cn("absolute w-2 h-2 bg-gray-900 rotate-45", {
              "bottom-[-4px] left-1/2 -translate-x-1/2": side === "top",
              "top-[-4px] left-1/2 -translate-x-1/2": side === "bottom",
              "right-[-4px] top-1/2 -translate-y-1/2": side === "left",
              "left-[-4px] top-1/2 -translate-y-1/2": side === "right",
            })}
          />
        </div>
      )}
    </div>
  );
}
